<template>
  <div>
    <!-- Modal -->
    <b-modal v-model="showModal" size="xl" title="Bid and Invoice Details" hide-footer>
      <template v-slot:modal-header="{ close }">
        <div class="row" style="width: 100%;">
          <div class="col-12 modal-header-content">
            <div>
              <i class="fa fa-dollar float-left" style="font-size: 32px;margin-right: 8px;color: #303032;"></i>
            </div>
            <div class="wd-modal-subheader">
              <h3 style="font-size: 18px;font-weight: 300;color: #303032;margin: 0;">Bid and Invoice Details</h3>
              <span v-if="job" style="font-size: 12px; color: #999;">Job #{{ job.job_number }} - {{ job.name }}</span>
            </div>
            <button type="button" class="close" @click="hide" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
      </template>

      <div class="wd-modal-body">
        <loading :active.sync="isLoading"
                 :can-cancel="false"
                 :width="50"
                 :height="50"
                 :is-full-page="false">
        </loading>

        <!-- Action Buttons -->
        <div class="row mb-3">
          <div class="col-md-12 text-right">
            <button class="btn btn-info btn-sm mr-2" @click="expandAllSections">
              <i class="fa fa-expand"></i> Expand All
            </button>
            <button class="btn btn-warning btn-sm mr-2" @click="collapseAllSections">
              <i class="fa fa-compress"></i> Collapse All
            </button>
            <button class="btn btn-secondary btn-sm" @click="refreshData">
              <i class="fa fa-refresh"></i> Refresh
            </button>
          </div>
        </div>

        <!-- Bid Line Items Section -->
        <div class="section-header" @click="toggleSection('bidLineItems')">
          <i :class="['fa', sections.bidLineItems ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
          <span>Bid Line Items ({{ bidLineItems.length }})</span>
        </div>
        <div v-show="sections.bidLineItems" class="accordion-section">
          <div v-if="bidLineItems.length === 0" class="text-center text-muted py-3">
            No bid line items found.
          </div>
          <div v-else class="accordion-container">
            <div v-for="(item, index) in bidLineItems" :key="'bid-' + index" class="accordion-item">
              <div class="accordion-header" @click="toggleAccordion('bid', index)">
                <div class="accordion-title">
                  <i :class="['fa', isExpanded('bid', index) ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
                  <span class="category-name">{{ item.category || 'N/A' }}</span>
                  <span class="amount-badge">${{ formatCurrency(item.amount) }}</span>
                </div>
              </div>
              <div v-if="isExpanded('bid', index)" class="accordion-content">
                <div class="detail-row">
                  <span class="detail-label">Product:</span>
                  <span class="detail-value">{{ item.product || 'N/A' }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Quantity:</span>
                  <span class="detail-value">{{ item.quantity || '0' }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Unit:</span>
                  <span class="detail-value">{{ item.unit || 'N/A' }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Rate:</span>
                  <span class="detail-value">${{ formatCurrency(item.rate) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Amount:</span>
                  <span class="detail-value">${{ formatCurrency(item.amount) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Taxable:</span>
                  <span class="detail-value">
                    <i v-if="item.tax === 'on' || item.taxable == 1" class="fa fa-check-circle text-success"></i>
                    <i v-else class="fa fa-times-circle text-danger"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary Section (After Bid Line Items) -->
        <div v-if="!isLoading" class="row mb-3 mt-3">
          <div class="col-md-4">
            <div class="summary-card">
              <span class="summary-label">Bid Subtotal</span>
              <span class="summary-value">${{ formatCurrency(bidSubTotal) }}</span>
            </div>
          </div>
          <div class="col-md-4">
            <div class="summary-card">
              <span class="summary-label">Sales Tax</span>
              <span class="summary-value">${{ formatCurrency(calculatedSalesTax) }}</span>
            </div>
          </div>
          <div class="col-md-4">
            <div class="summary-card grand-total">
              <span class="summary-label">Grand Total</span>
              <span class="summary-value text-success">${{ formatCurrency(grandTotal) }}</span>
            </div>
          </div>
        </div>

        <!-- Bid Comments Section -->
        <div class="section-header" @click="toggleSection('bidComments')">
          <i :class="['fa', sections.bidComments ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
          <span>Bid Comments ({{ bidComments.length }})</span>
        </div>
        <div v-show="sections.bidComments" class="accordion-section">
          <div v-if="bidComments.length === 0" class="text-center text-muted py-3">
            No bid comments found.
          </div>
          <div v-else class="accordion-container">
            <div v-for="(comment, index) in bidComments" :key="'comment-' + index" class="accordion-item">
              <div class="accordion-header" @click="toggleAccordion('comment', index)">
                <div class="accordion-title">
                  <i :class="['fa', isExpanded('comment', index) ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
                  <span class="category-name">Comment #{{ index + 1 }}</span>
                </div>
              </div>
              <div v-if="isExpanded('comment', index)" class="accordion-content">
                <div class="detail-row">
                  <span class="detail-value comment-text">{{ comment.comment || 'No comment text' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Past Invoices Section -->
        <div class="section-header" @click="toggleSection('pastInvoices')">
          <i :class="['fa', sections.pastInvoices ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
          <span>Past Invoices ({{ pastInvoices.length }})</span>
        </div>
        <div v-show="sections.pastInvoices" class="accordion-section">
          <div v-if="pastInvoices.length === 0" class="text-center text-muted py-3">
            No past invoices found.
          </div>
          <div v-else class="accordion-container">
            <div v-for="(invoice, index) in pastInvoices" :key="'invoice-' + index" class="accordion-item">
              <div class="accordion-header" @click="toggleAccordion('invoice', index)">
                <div class="accordion-title">
                  <i :class="['fa', isExpanded('invoice', index) ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
                  <span class="category-name">Invoice #{{ invoice.invoice_number || index + 1 }}</span>
                  <span class="amount-badge">${{ formatCurrency(invoice.grand_total) }}</span>
                </div>
              </div>
              <div v-if="isExpanded('invoice', index)" class="accordion-content">
                <div class="detail-row">
                  <span class="detail-label">Invoice Date:</span>
                  <span class="detail-value">{{ formatDate(invoice.created_at) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Due Date:</span>
                  <span class="detail-value">{{ formatDate(invoice.due_date) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Subtotal:</span>
                  <span class="detail-value">${{ formatCurrency(invoice.subtotal) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Sales Tax:</span>
                  <span class="detail-value">${{ formatCurrency(invoice.sales_tax_total) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Grand Total:</span>
                  <span class="detail-value">${{ formatCurrency(invoice.grand_total) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Invoice Type:</span>
                  <span class="detail-value">{{ invoice.invoice_type || 'N/A' }}</span>
                </div>
                <div v-if="invoice.comment" class="detail-row">
                  <span class="detail-label">Comment:</span>
                  <span class="detail-value">{{ invoice.comment }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Payments Section -->
        <div class="section-header" @click="toggleSection('payments')">
          <i :class="['fa', sections.payments ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
          <span>Payments ({{ jobPayments.length }})</span>
        </div>
        <div v-show="sections.payments" class="accordion-section">
          <div v-if="jobPayments.length === 0" class="text-center text-muted py-3">
            No payments found.
          </div>
          <div v-else class="accordion-container">
            <div v-for="(payment, index) in jobPayments" :key="'payment-' + index" class="accordion-item">
              <div class="accordion-header" @click="toggleAccordion('payment', index)">
                <div class="accordion-title">
                  <i :class="['fa', isExpanded('payment', index) ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
                  <span class="category-name">Payment {{ formatDate(payment.payment_date) }}</span>
                  <span class="amount-badge text-success">${{ formatCurrency(payment.payment_amount) }}</span>
                </div>
              </div>
              <div v-if="isExpanded('payment', index)" class="accordion-content">
                <div class="detail-row">
                  <span class="detail-label">Payment Date:</span>
                  <span class="detail-value">{{ formatDate(payment.payment_date) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Amount:</span>
                  <span class="detail-value">${{ formatCurrency(payment.payment_amount) }}</span>
                </div>
                <div v-if="payment.payment_method" class="detail-row">
                  <span class="detail-label">Method:</span>
                  <span class="detail-value">{{ payment.payment_method }}</span>
                </div>
                <div v-if="payment.notes" class="detail-row">
                  <span class="detail-label">Notes:</span>
                  <span class="detail-value">{{ payment.notes }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No Data Message -->
        <div v-if="!isLoading && bidLineItems.length === 0 && bidComments.length === 0 && pastInvoices.length === 0 && jobPayments.length === 0" class="row">
          <div class="col-12 text-center py-4">
            <i class="fa fa-info-circle" style="font-size: 48px; color: #ccc;"></i>
            <p class="mt-3 text-muted">No bid or invoice data available for this job.</p>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import CommonConstants from '@/constants/constants.js';
import moment from 'moment';

export default {
  name: 'BidInvoiceJobModal',
  components: {
    Loading
  },
  props: {
    jobId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      constants: CommonConstants,
      isLoading: false,
      showModal: false,
      job: null,
      bid: null,
      bidLineItems: [],
      bidComments: [],
      pastInvoices: [],
      jobPayments: [],
      taxRatePercent: '0.00',
      sections: {
        bidLineItems: true,
        bidComments: true,
        pastInvoices: true,
        payments: true
      },
      expandedItems: {
        bid: {},
        comment: {},
        invoice: {},
        payment: {}
      }
    };
  },
  computed: {
    header() {
      return {
        headers: {
          'Content-type': "application/json",
          'token': localStorage.getItem('token')
        }
      };
    },
    bidSubTotal() {
      let total = 0;
      this.bidLineItems.forEach(item => {
        total += Number(item.amount) || 0;
      });
      return total.toFixed(2);
    },
    calculatedSalesTax() {
      let tax = 0;
      this.bidLineItems.forEach(item => {
        if (item.tax === 'on' || item.taxable == 1) {
          tax += (Number(item.amount) * Number(this.taxRatePercent)) / 100;
        }
      });
      return tax.toFixed(2);
    },
    grandTotal() {
      return (Number(this.bidSubTotal) + Number(this.calculatedSalesTax)).toFixed(2);
    }
  },
  methods: {
    show() {
      this.showModal = true;
      this.fetchBidInvoiceData();
    },
    hide() {
      this.showModal = false;
    },
    async fetchBidInvoiceData() {
      this.isLoading = true;
      try {
        const response = await axios.get(
          this.constants.api_url + 'job/get/bidData/' + this.jobId,
          this.header
        );

        if (response.data && response.data.data) {
          const data = response.data.data;
          this.job = data.job || null;
          this.bid = data.bid || null;
          this.bidLineItems = data.bid_line_items === false ? [] : (data.bid_line_items || []);
          this.bidComments = data.bid_comments || [];
          this.pastInvoices = data.invoices || [];
          this.jobPayments = data.job_payments || [];

          // Set tax rate if available from bid
          if (this.bid && this.bid.tax_rate) {
            this.taxRatePercent = this.bid.tax_rate;
          }

          // Mark taxable items
          this.bidLineItems.forEach(item => {
            if (item.taxable == 1) {
              item.tax = 'on';
            }
          });

          // Auto-expand all accordion items by default
          this.expandedItems = {
            bid: {},
            comment: {},
            invoice: {},
            payment: {}
          };

          // Expand all bid line items by default
          this.bidLineItems.forEach((_, index) => {
            this.$set(this.expandedItems.bid, index, true);
          });
          this.bidComments.forEach((_, index) => {
            this.$set(this.expandedItems.comment, index, true);
          });
          this.pastInvoices.forEach((_, index) => {
            this.$set(this.expandedItems.invoice, index, true);
          });
          this.jobPayments.forEach((_, index) => {
            this.$set(this.expandedItems.payment, index, true);
          });
        }
      } catch (error) {
        console.error('Error fetching bid and invoice data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    toggleSection(section) {
      this.sections[section] = !this.sections[section];
    },
    isExpanded(type, index) {
      return this.expandedItems[type] && this.expandedItems[type][index] === true;
    },
    toggleAccordion(type, index) {
      const currentValue = this.expandedItems[type][index] || false;
      this.$set(this.expandedItems[type], index, !currentValue);
      this.$forceUpdate();
    },
    expandAllSections() {
      this.sections = {
        bidLineItems: true,
        bidComments: true,
        pastInvoices: true,
        payments: true
      };

      // Expand all accordion items
      this.bidLineItems.forEach((_, index) => {
        this.$set(this.expandedItems.bid, index, true);
      });
      this.bidComments.forEach((_, index) => {
        this.$set(this.expandedItems.comment, index, true);
      });
      this.pastInvoices.forEach((_, index) => {
        this.$set(this.expandedItems.invoice, index, true);
      });
      this.jobPayments.forEach((_, index) => {
        this.$set(this.expandedItems.payment, index, true);
      });
    },
    collapseAllSections() {
      // Collapse all accordion items
      this.expandedItems = {
        bid: {},
        comment: {},
        invoice: {},
        payment: {}
      };
    },
    refreshData() {
      this.fetchBidInvoiceData();
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '0.00';
      return Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('MM/DD/YYYY');
    }
  }
};
</script>

<style scoped>
.wd-modal-body {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.wd-modal-subheader {
  display: inline-block;
  vertical-align: top;
  margin-top: 4px;
}

/* Summary Cards */
.summary-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  margin-bottom: 10px;
}

.summary-card.grand-total {
  background: #e8f5e9;
}

.summary-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 5px;
}

.summary-value {
  display: block;
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

/* Section Headers */
.section-header {
  background: #f0f0f0;
  padding: 12px 15px;
  margin-top: 15px;
  margin-bottom: 5px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
}

.section-header:hover {
  background: #e5e5e5;
}

.section-header i {
  margin-right: 10px;
  width: 15px;
}

/* Accordion Styles */
.accordion-section {
  margin-bottom: 10px;
}

.accordion-container {
  border: 1px solid #ddd;
  border-radius: 5px;
  overflow: hidden;
}

.accordion-item {
  border-bottom: 1px solid #eee;
}

.accordion-item:last-child {
  border-bottom: none;
}

.accordion-header {
  padding: 12px 15px;
  background: #fff;
  cursor: pointer;
  transition: background-color 0.2s;
}

.accordion-header:hover {
  background: #f9f9f9;
}

.accordion-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.accordion-title i {
  margin-right: 10px;
  color: #6495ED;
  width: 15px;
}

.category-name {
  flex: 1;
  font-weight: 500;
  color: #333;
  min-width: 150px;
}

.amount-badge {
  background: #e3f2fd;
  padding: 3px 10px;
  border-radius: 15px;
  font-size: 13px;
  font-weight: bold;
  color: #1976d2;
}

/* Remove any + icon from amount-badge */
.accordion-title::before,
.accordion-title::after {
  display: none !important;
  content: none !important;
}

.amount-badge.text-success {
  background: #e8f5e9;
  color: #2e7d32;
}

/* Accordion Content */
.accordion-content {
  padding: 15px;
  background: #fafafa;
  border-top: 1px solid #eee;
  display: block !important;
  visibility: visible !important;
  height: auto !important;
  overflow: visible !important;
  opacity: 1 !important;
}

.detail-row {
  display: flex;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: #555;
  width: 120px;
  min-width: 120px;
  flex-shrink: 0;
}

.detail-value {
  color: #333;
  flex: 1;
  word-break: break-word;
}

.comment-text {
  white-space: pre-wrap;
  line-height: 1.5;
}

/* Button Styles */
.btn {
  margin-right: 5px;
}

/* Text Colors */
.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .wd-modal-body {
    padding: 10px;
  }

  .summary-card {
    padding: 10px;
    margin-bottom: 5px;
  }

  .summary-value {
    font-size: 16px;
  }

  .section-header {
    padding: 10px 12px;
    font-size: 14px;
  }

  .accordion-header {
    padding: 10px 12px;
  }

  .accordion-title {
    font-size: 14px;
  }

  .category-name {
    min-width: 100px;
  }

  .amount-badge {
    font-size: 12px;
    padding: 2px 8px;
    margin-top: 5px;
  }

  .accordion-content {
    padding: 10px;
  }

  .detail-label {
    margin-bottom: 3px;
    min-width: auto;
  }

  .text-right {
    text-align: left !important;
    margin-bottom: 10px;
  }

  .btn-sm {
    padding: 5px 10px;
    font-size: 12px;
    margin-bottom: 5px;
  }
}

@media (max-width: 576px) {
  .summary-card {
    margin-bottom: 10px;
  }

  .col-md-4 {
    margin-bottom: 10px;
  }
}

/* Remove extra space on the right of the modal header */
.modal-header {
  padding-right: 0 !important;
  margin-right: 0 !important;
}

/* Ensure the modal header spans the full width */
.modal-header .row {
  width: 100%;
  margin: 0;
}

.modal-header-content{
  display:flex;
  align-items:flex-start;
}
</style>
